<template>
  <ValidationObserver>
    <b-form-radio-group
      v-model="schoolRecommendationSelected"
      :disabled="readonly"
    >
      <b-form-radio
        class="ml-lg-4"
        :value="SchoolRecommendationTypes.SameMunicipality"
      >
        <div class="font-weight-normal">
          <span>
            Se ofrece la(s) siguiente(s) recomendación(es) de localización en el
            municipio de residencia.
          </span>
          <span class="font-italic">
            (La obligación del DEPR es ofrecer una localización lo más cercano
            al hogar del estudiante. Si el personal de la ORE identifica más de
            una escuela donde se tiene la alternativa de ubicación recomendada,
            esta(s) deben ser documentadas a continuación.)
          </span>
        </div>
      </b-form-radio>

      <div
        v-if="
          schoolRecommendationSelected ===
            SchoolRecommendationTypes.SameMunicipality
        "
        :key="
          `SchoolRecommendation_${SchoolRecommendationTypes.SameMunicipality}`
        "
        class="pt-2"
      >
        <ValidationProvider
          v-slot="{ errors, validate }"
          :rules="{ required: validateItems }"
          :custom-messages="{
            required: 'Favor recomendar la alternativa de ubicación.',
          }"
        >
          <SchoolRecommendation
            :readonly="readonly"
            :academic-year-id="academicYearId"
            :tap-request-id="tapRequestId"
            :tap-request-type-id="tapRequestTypeId"
            :region="region"
            :municipality="municipality"
            :items.sync="itemsWithMunicipality"
            @input="validate"
          />
          <Error v-if="errors[0]" variant="no-border" :message="errors[0]" />
        </ValidationProvider>
      </div>

      <div class="my-2" />

      <b-form-radio
        class="ml-lg-4"
        :value="SchoolRecommendationTypes.OtherMunicipality"
      >
        <div class="font-weight-normal">
          <span>
            La alternativa de ubicación recomendada por el COMPU no está
            disponible en el municipio de residencia, pero se identifica en otro
            municipio de la ORE.
          </span>
        </div>
      </b-form-radio>

      <div
        v-if="
          schoolRecommendationSelected ===
            SchoolRecommendationTypes.OtherMunicipality
        "
        :key="
          `SchoolRecommendation_${SchoolRecommendationTypes.OtherMunicipality}`
        "
        class="pt-2"
      >
        <ValidationProvider
          v-slot="{ errors, validate }"
          :rules="{ required: validateItems }"
          :custom-messages="{
            required: 'Favor recomendar la alternativa de ubicación.',
          }"
        >
          <SchoolRecommendation
            :readonly="readonly"
            :academic-year-id="academicYearId"
            :tap-request-id="tapRequestId"
            :tap-request-type-id="tapRequestTypeId"
            :region="region"
            :municipality="municipality"
            select-municipality
            exclude-municipality
            :items.sync="itemsWithoutMunicipality"
            @input="validate"
          />
          <Error v-if="errors[0]" variant="no-border" :message="errors[0]" />
        </ValidationProvider>
      </div>

      <template v-if="enableLocationNotAvailableOption">
        <div class="my-2" />

        <b-form-radio
          class="ml-lg-4"
          :value="SchoolRecommendationTypes.NotAvailable"
        >
          <div class="font-weight-normal">
            <span>
              La alternativa de ubicación recomendada por el COMPU no está
              disponible en la ORE de residencia. La ORE desarrollará un plan de
              acción para atender esta necesidad.
            </span>
          </div>
        </b-form-radio>

        <div
          v-if="
            schoolRecommendationSelected ===
              SchoolRecommendationTypes.NotAvailable &&
              $slots['location-not-available']
          "
          :key="
            `SchoolRecommendation_${SchoolRecommendationTypes.NotAvailable}`
          "
          class="pt-2"
        >
          <slot name="location-not-available" />
        </div>
      </template>
    </b-form-radio-group>

    <ValidationProvider
      v-slot="{ errors }"
      :disabled="!validateOptions"
      :rules="{ required: true }"
      :custom-messages="{
        required:
          'Favor seleccionar el proceso de búsqueda de localizaciones disponibles.',
      }"
    >
      <input
        v-model="schoolRecommendationSelected"
        type="hidden"
        required
      />
      <Error v-if="errors[0]" variant="no-border" :message="errors[0]" />
    </ValidationProvider>
  </ValidationObserver>
</template>

<script>
import Error from 'custom-error';
import { SchoolRecommendation } from '@/components/pei/ubication-tech-assistance/_components';

export default {
  name: 'AvailableLocations',
  components: { Error, SchoolRecommendation },
  inject: { tapRequestId: { default: 0 } },
  props: {
    academicYearId: { type: [Number, String], required: true, default: null },
    tapRequestTypeId: { type: [Number, String], required: true, default: null },
    region: { type: Object, required: true, default: null },
    municipality: { type: Object, required: true, default: null },
    items: { type: Array, required: true, default: () => [] },
    enableLocationNotAvailableOption: { type: Boolean, default: false },
    validateOptions: { type: Boolean, default: false },
    validateItems: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
    hasItemsWithoutAvailability: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data: () => ({
    SchoolRecommendationTypes: {
      SameMunicipality: 'same-municipality',
      OtherMunicipality: 'other-municipality',
      NotAvailable: 'not-available',
    },
    schoolRecommendationSelected: null,
    itemsWithMunicipality: [],
    itemsWithoutMunicipality: [],
  }),
  methods: {
    syncItems() {
      const value = [
        ...this.itemsWithMunicipality,
        ...this.itemsWithoutMunicipality,
      ];
      this.$emit('update:items', value);
      this.$emit('input', value.length);
    },
  },
  watch: {
    items: {
      handler(value) {
        if (!value?.length) return;
        if (!this.itemsWithMunicipality?.length) {
          const itemsWithMunicipality =
            value?.filter(
              ({ municipality }) => municipality?.id === this.municipality?.id
            ) || [];
          if (itemsWithMunicipality.length) {
            this.itemsWithMunicipality = itemsWithMunicipality;
          }
        }
        if (!this.itemsWithoutMunicipality?.length) {
          const itemsWithoutMunicipality =
            value.filter(
              ({ municipality }) => municipality?.id !== this.municipality?.id
            ) || [];
          if (itemsWithoutMunicipality.length) {
            this.itemsWithoutMunicipality = itemsWithoutMunicipality;
          }
        }
      },
      deep: true,
      immediate: true,
    },
    itemsWithMunicipality: {
      handler() {
        this.syncItems();
      },
      deep: true,
    },
    itemsWithoutMunicipality: {
      handler() {
        this.syncItems();
      },
      deep: true,
    },
  },
  created() {
    if (this.itemsWithMunicipality.length) {
      this.schoolRecommendationSelected = this.SchoolRecommendationTypes.SameMunicipality;
    }
    if (this.itemsWithoutMunicipality.length) {
      this.schoolRecommendationSelected = this.SchoolRecommendationTypes.OtherMunicipality;
    }
    if (this.hasItemsWithoutAvailability) {
      this.schoolRecommendationSelected = this.SchoolRecommendationTypes.NotAvailable;
    }
  },
};
</script>
